/**
 * URL helpers ported (and slimmed) from legacy `public/js/utils/url-utils.js`.
 * The legacy locale-param juggling / DOMPurify path is out of scope for the
 * CardDetails tree, so these keep just the behaviour the ported components need.
 */
import qs from 'query-string'
import { CLIENT_ROUTE_PATHS } from '@/shared/utils/routes'
import { PP_SUMMARY } from 'universal/routes'
import { serverData } from 'utils/server-data'
import { UCS_STUB_URL, PRODUCTS } from './constants'

const { BDMC_ENROLL, BDMC_ENROLL_ADDITIONAL, REWARDS, BDMC_RECURRING_PAYMENTS, CHANGE_ADDRESS } =
  CLIENT_ROUTE_PATHS
const LEGACY_BDMC_SUMMARY_PATTERN = /\/myaccount\/debit-card(\/v2)?\/summary/

export function shouldUseConsolidatedBdmcSummaryReturn(returnUri?: string): boolean {
  if (!returnUri) {
    return true
  }

  if (LEGACY_BDMC_SUMMARY_PATTERN.test(returnUri)) {
    return true
  }

  if (returnUri === PP_SUMMARY || returnUri.startsWith(`${PP_SUMMARY}?`)) {
    return true
  }

  return false
}

/** Exit target for BDMC flows that should return to card summary (not PP /myaccount/summary). */
export function resolveBdmcSummaryExitHref(
  queryParams: Record<string, string> = {},
  productName = queryParams.productName || queryParams.product_name || PRODUCTS.BUSINESS_DEBIT_CARD,
): string {
  const { returnUri } = queryParams

  if (serverData.isBdmcExperience === true && productName === PRODUCTS.BUSINESS_DEBIT_CARD) {
    if (shouldUseConsolidatedBdmcSummaryReturn(returnUri)) {
      return prepBdmcSummaryLink()
    }

    return returnUri
  }

  if (returnUri) {
    return returnUri
  }

  return serverData.homeFallbackURI || `${UCS_STUB_URL}/summary?${qs.stringify({ productName })}`
}

export function navigateToUrl(url: string): void {
  if (typeof window !== 'undefined' && window.location && window.location.replace) {
    window.location.replace(url)
  }
}

export function browserRedirectTo(url: string): void {
  if (typeof window !== 'undefined' && window.location) {
    window.location.href = url
  }
}

export function prepBdmcSummaryLink(): string {
  return `${UCS_STUB_URL}/summary?productName=${PRODUCTS.BUSINESS_DEBIT_CARD}`
}

/**
 * Preserve Venice/OSLO native webview signals across full-page BDMC navigations.
 * Balance-hub / summary often open with platform / webviewCallback; dropping them
 * on enroll links can stamp sticky isMobileNative=false and blank the value-prop path.
 */
function getNativeWebviewQueryParams(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {}
  }

  const search = qs.parse(window.location.search) || {}
  const params: Record<string, string> = {}

  ;(['webview', 'webviewCallback', 'platform'] as const).forEach((key) => {
    const value = search[key]
    if (typeof value === 'string' && value) {
      params[key] = value
    }
  })

  return params
}

/** Summary URL for full-page reload after lock/unlock, preserving the displayed card. */
export function prepBdmcSummaryReloadLink(cardId: string): string {
  if (!cardId) {
    return prepBdmcSummaryLink()
  }

  return `${prepBdmcSummaryLink()}&cardId=${encodeURIComponent(cardId)}`
}

/** Rewards / cashback dashboard always returns to BDMC summary (no client returnUri). */
export function resolveBdmcRewardsExitHref(_queryParams: Record<string, string> = {}): string {
  return prepBdmcSummaryLink()
}

export function prepBdmcRecurringPaymentsLink(): string {
  return `${UCS_STUB_URL}${BDMC_RECURRING_PAYMENTS}?${qs.stringify({
    productName: PRODUCTS.BUSINESS_DEBIT_CARD,
  })}`
}

/** Autopay / activities dashboard always returns to BDMC summary (no client returnUri). */
export function resolveBdmcRecurringPaymentsExitHref(
  _queryParams: Record<string, string> = {},
): string {
  return prepBdmcSummaryLink()
}

export function prepBdmcEditBillingAddressLink(cardId: string): string {
  if (!cardId) {
    return ''
  }

  // Venice/OSLO open summary with ?webview=true (+ platform / callback). Keep those
  // signals so apiPath / isMobileNative stay consistent in the new shell.
  return `${UCS_STUB_URL}${CHANGE_ADDRESS}?${qs.stringify({
    productName: PRODUCTS.BUSINESS_DEBIT_CARD,
    cardId,
    addressType: 'BILLING',
    ...getNativeWebviewQueryParams(),
  })}`
}

/** Edit-billing always returns to BDMC summary (no client returnUri / open-redirect). */
export function resolveBdmcEditBillingAddressExitHref(
  _queryParams: Record<string, string> = {},
): string {
  return prepBdmcSummaryLink()
}

export function prepBdmcEnrollLink(
  kind: 'primary' | 'additional',
  returnUri: string = prepBdmcSummaryLink(),
): string {
  const path = kind === 'additional' ? BDMC_ENROLL_ADDITIONAL : BDMC_ENROLL
  return `${UCS_STUB_URL}${path}?${qs.stringify({
    productName: PRODUCTS.BUSINESS_DEBIT_CARD,
    returnUri,
    ...getNativeWebviewQueryParams(),
  })}`
}

export function resolveBdmcEnrollExitHref(queryParams: Record<string, string> = {}): string {
  const returnUri = queryParams.returnUri || queryParams.externalReturnUri

  if (returnUri) {
    return returnUri
  }

  return prepBdmcSummaryLink()
}

export function prepBdmcRewardsLink(): string {
  return `${UCS_STUB_URL}${REWARDS}?${qs.stringify({
    productName: PRODUCTS.BUSINESS_DEBIT_CARD,
  })}`
}

type BdmcCardLimitsExitCard = {
  primary_card?: boolean
  card_id?: string
}

export function prepBdmcCardLimitsLink(cardId: string): string {
  if (!cardId) {
    return ''
  }

  return `${UCS_STUB_URL}/card-limits/${cardId}?${qs.stringify({
    productName: PRODUCTS.BUSINESS_DEBIT_CARD,
  })}`
}

/** Daily limits always returns to BDMC summary (no client returnUri). */
export function resolveBdmcCardLimitsExitHref(
  _queryParams: Record<string, string> = {},
  card?: BdmcCardLimitsExitCard | null,
): string {
  if (card && card.primary_card === false && card.card_id) {
    return `${prepBdmcSummaryLink()}&cardId=${encodeURIComponent(card.card_id)}`
  }

  return prepBdmcSummaryLink()
}

export function resolveBdmcActivateExitHref(queryParams: Record<string, string> = {}): string {
  const returnUri = queryParams.returnUri || queryParams.externalReturnUri

  if (returnUri) {
    return returnUri
  }

  return prepBdmcSummaryLink()
}

export function prepBdmcActivateLink(
  cardId: string,
  returnUri: string = prepBdmcSummaryLink(),
): string {
  // Always UCS activate URL; server consolidation gate bounces to dcnw when Elmo is off.
  const params = new URLSearchParams({
    productName: PRODUCTS.BUSINESS_DEBIT_CARD,
    returnUri,
    cancelUri: returnUri,
  })
  return `${UCS_STUB_URL}/activate/${encodeURIComponent(cardId)}?${params.toString()}`
}

export function prepBdmcShowCardLink(cardId: string): string {
  if (!cardId) {
    return ''
  }

  const returnUri = encodeURIComponent(prepBdmcSummaryLink())
  return `${UCS_STUB_URL}/manage/${cardId}/show-card?returnUri=${returnUri}`
}

/** Redirect to `path` carrying the current query string forward. */
export function redirectToWithQueryParams(path = ''): void {
  const search = typeof window !== 'undefined' ? window.location.search : ''
  if (!search) {
    browserRedirectTo(path)
    return
  }
  const separator = path.includes('?') ? '&' : '?'
  browserRedirectTo(`${path}${separator}${search.slice(1)}`)
}
